import type { ConnectorStyle, Handle, HandlePosition, Point, ShapeType } from '../state/types';
import { DEFAULT_CONNECTOR_STYLE } from '../state/types';
import { buildElementJSON } from '../state/elementSerialization';
import { generateId } from '../../../utils/id';
import {
  getConnectorPoints,
  getConnectorTextPosition,
  pointToBezierDistance,
  pointToSegmentDistance,
  type ConnectorGeometryInput,
} from '../connector/ConnectorGeometry';
import { renderConnector } from '../connector/ConnectorRenderer';

export class Connector {
  readonly id: string;
  readonly type: ShapeType = 'connector';
  startPoint: Point;
  endPoint: Point;
  waypoints: Point[];
  startShapeId: string | null;
  endShapeId: string | null;
  startConnectionPointId: string | null;
  endConnectionPointId: string | null;
  style: ConnectorStyle;
  selected: boolean;
  text: string;
  textSize: number;
  arcPoint: Point | null;

  constructor(startPoint: Point, endPoint: Point, style?: Partial<ConnectorStyle>) {
    this.id = generateId('conn');
    this.startPoint = { ...startPoint };
    this.endPoint = { ...endPoint };
    this.waypoints = [];
    this.startShapeId = null;
    this.endShapeId = null;
    this.startConnectionPointId = null;
    this.endConnectionPointId = null;
    this.style = { ...DEFAULT_CONNECTOR_STYLE, ...style };
    this.selected = false;
    this.text = '';
    this.textSize = 16;
    this.arcPoint = null;
  }

  private getGeometryInput(): ConnectorGeometryInput {
    return {
      startPoint: this.startPoint,
      endPoint: this.endPoint,
      waypoints: this.waypoints,
      arcPoint: this.arcPoint,
    };
  }

  getAllPoints(): Point[] {
    return getConnectorPoints(this.getGeometryInput());
  }

  containsPoint(p: Point, threshold = 8): boolean {
    if (this.arcPoint && this.waypoints.length === 0) {
      return (
        pointToBezierDistance(p, this.startPoint, this.arcPoint, this.endPoint) <= threshold
      );
    }

    const points = this.getAllPoints();

    for (let i = 0; i < points.length - 1; i++) {
      const start = points[i]!;
      const end = points[i + 1]!;
      if (pointToSegmentDistance(p, start, end) <= threshold) {
        return true;
      }
    }
    return false;
  }

  getHandles(): Handle[] {
    const handles: Handle[] = [
      { position: 'top-left', point: this.startPoint },
      { position: 'bottom-right', point: this.endPoint },
    ];

    if (this.arcPoint) {
      handles.push({
        position: 'top' as HandlePosition,
        point: this.arcPoint,
      });
    }

    this.waypoints.forEach((wp, i) => {
      handles.push({
        position: `waypoint-${i}` as HandlePosition,
        point: wp,
      });
    });

    return handles;
  }

  getHandleAtPoint(p: Point, scale = 1): HandlePosition | null {
    const threshold = 12 / scale;
    if (Math.hypot(p.x - this.startPoint.x, p.y - this.startPoint.y) <= threshold) {
      return 'top-left';
    }
    if (Math.hypot(p.x - this.endPoint.x, p.y - this.endPoint.y) <= threshold) {
      return 'bottom-right';
    }

    if (this.arcPoint && Math.hypot(p.x - this.arcPoint.x, p.y - this.arcPoint.y) <= threshold) {
      return 'top';
    }

    for (let i = 0; i < this.waypoints.length; i++) {
      const wp = this.waypoints[i]!;
      if (Math.hypot(p.x - wp.x, p.y - wp.y) <= threshold) {
        return `waypoint-${i}` as HandlePosition;
      }
    }

    return null;
  }

  addWaypoint(point: Point, afterIndex = -1): void {
    if (afterIndex === -1) {
      const points = this.getAllPoints();
      let bestIndex = 0;
      let bestDist = Infinity;

      for (let i = 0; i < points.length - 1; i++) {
        const dist = pointToSegmentDistance(point, points[i]!, points[i + 1]!);
        if (dist < bestDist) {
          bestDist = dist;
          bestIndex = i;
        }
      }
      this.waypoints.splice(bestIndex, 0, point);
    } else {
      this.waypoints.splice(afterIndex, 0, point);
    }
  }

  removeWaypoint(index: number): void {
    this.waypoints.splice(index, 1);
  }

  render(ctx: CanvasRenderingContext2D, inverted = false): void {
    renderConnector(ctx, this, inverted);
  }

  getTextPosition(): Point {
    return getConnectorTextPosition(this.getGeometryInput());
  }

  move(delta: Point): void {
    this.startPoint.x += delta.x;
    this.startPoint.y += delta.y;
    this.endPoint.x += delta.x;
    this.endPoint.y += delta.y;
    if (this.arcPoint) {
      this.arcPoint.x += delta.x;
      this.arcPoint.y += delta.y;
    }
    for (const wp of this.waypoints) {
      wp.x += delta.x;
      wp.y += delta.y;
    }
  }

  toJSON(): object {
    return buildElementJSON(this.id, this.type, {
      startPoint: this.startPoint,
      endPoint: this.endPoint,
      waypoints: this.waypoints,
      startShapeId: this.startShapeId,
      endShapeId: this.endShapeId,
      startConnectionPointId: this.startConnectionPointId,
      endConnectionPointId: this.endConnectionPointId,
      style: this.style,
      text: this.text,
      textSize: this.textSize,
      arcPoint: this.arcPoint,
    });
  }
}
